/**
 * TEMPL — Cadence-style Subscribe & Save widget
 * ==============================================
 * Injects the two elements Loop does not render, so CSS can do the rest:
 *   1. a "-15%" pill next to the "Subscribe & Save" label
 *   2. the "Subscription includes" benefit card (photo, title, struck price -> FREE)
 *
 * Everything else in the Cadence look — option borders, price treatment, the
 * framed frequency dropdown — is pure CSS in templ-subscription-widget.css,
 * loaded from snippets/stylesheets.liquid.
 * Keep it that way: CSS survives Loop re-renders for free, injected DOM does not.
 *
 * SEPARATE FROM templ-subscription-gift.js ON PURPOSE.
 * That file owns cart reconciliation and is verified. This file is presentation
 * only and cannot break checkout.
 *
 * PROMO GATING — READ THIS BEFORE CHANGING IT
 * -------------------------------------------
 * Earlier revisions gated the card on <html data-templ-flag-promo>, on the
 * assumption that templ-subscription-gift.js set and cleared that attribute from
 * Shopify's clock. IT DOES NOT. That file is cart reconciliation only and never
 * touches documentElement — its own header says PDP messaging lives elsewhere.
 * With that gate in place the card would have silently never rendered.
 *
 * So the card is now controlled by CONFIG.showBenefitCard below: a plain
 * boolean, defaulting to ON.
 *
 * NOTHING TURNS IT OFF AUTOMATICALLY. When the flag promo ends, either flip that
 * boolean to false or remove this file's import. If you want it to expire on its
 * own, see the commented date-window helper under promoIsLive().
 *
 * The "-15%" pill is NOT gated. The subscription discount is permanent; only the
 * free gift is a limited-time promo.
 *
 * LOOP DOM (verified on buildtempl.com, Aug 16 2026)
 * -------------------------------------------------
 * .loop-widget-purchase-options-container            flex column, gap 10px
 *   .loop-widget-purchase-option[data-loop-widget-selling-plan-group]
 *     .loop-widget-purchase-option-header
 *       .loop-widget-spg-label-discount-wrapper      <- pill goes here
 *         .loop-widget-purchase-option-radio
 *         .loop-widget-purchase-option-label
 *       .loop-widget-purchase-option-price-each-container
 *     .loop-widget-spg-container.expanded            <- benefit card goes here
 *       .loop-widget-sp-selector-description-wrapper (hidden by CSS)
 *       .loop-widget-sp-selector-wrapper             (the 30/45/60 select)
 *   .loop-widget-purchase-option-onetime
 */

const CONFIG = {
  /* Fallback only. The pill percentage is computed from the rendered prices at
     runtime (see computeDiscountLabel), so it can never go stale if the plan
     discount changes. This value shows only if the prices can't be parsed. */
  discountLabel: '-15%',

  /* Cadence puts a "Month 1" eyebrow above each gift title. Removed at Carson's
     request Aug 16. Note the side effect: nothing in the widget now states WHEN
     the flag arrives — the cart and confirmation carry that instead. */
  gift: {
    image: 'https://cdn.shopify.com/s/files/1/0908/9948/0861/files/FlagProductCarousel.png?v=1785253872&width=128',
    title: '"LET MY TRAINING BE WORSHIP" Flag',
    wasPrice: '$24.99',
    nowPrice: 'FREE',
  },

  // Mirrors Cadence's header: "Subscription includes: 3 FREE gifts and -15% every month"
  headerPrefix: 'Subscription includes: 1',
  headerStrong: 'FREE',
  headerMiddle: 'gift and',
  headerSuffix: 'every order',

  // Cadence's dot-separated footer strip. Replaces Loop's stock description
  // lines, which CSS hides. Wording and order set by Carson Aug 16 —
  // reassurance first, then the standing discount.
  footerItems: [
    'Cancel anytime, no commitment required',
    '15% off every order',
  ],

  startExpanded: true,

  /* Master switch for the gift card. See PROMO GATING in the header — nothing
     flips this for you when the promo ends. */
  showBenefitCard: false,
};

/* STYLESHEET LOADING — deliberately NOT done here.
 *
 * An earlier build injected the stylesheet from this file using
 * new URL('./templ-subscription-widget.css', import.meta.url). It loaded, but
 * that URL carries no ?v= hash, so Shopify's CDN pinned the first version
 * forever — a cache-busted fetch on Aug 16 still returned the previous build
 * after the file had been updated. Edits appeared to do nothing.
 *
 * The stylesheet is now loaded from snippets/stylesheets.liquid via asset_url,
 * which versions the URL automatically. Do not reintroduce the JS injection.
 */

const SUB_OPTION = '.loop-widget-purchase-option[data-loop-widget-selling-plan-group]';
const LABEL_WRAP = '.loop-widget-spg-label-discount-wrapper';
const MOUNT = '.loop-widget-spg-container';
const PILL_CLASS = 'templ-pill';
const CARD_CLASS = 'templ-benefits';

/* PREREQUISITE — two blocks must NOT be present in theme.liquid, just above the
   closing body tag. Confirmed already removed from the draft theme Aug 16:

     1. a style block commented "Subscribe & Save Savings Color", targeting
        .loop-widget-purchase-option-price-container[data-savings]::after
     2. a script block commented "Loop Subscribe & Save — Calculate and display
        savings"

   They are a pair: the script computes the dollar saving and writes it to a
   data-savings attribute, the CSS renders it via content: attr(data-savings).
   The "-15%" pill below replaces both, computed from the rendered prices.

   The script also self-triggers: it observes {attributes: true} across the whole
   body subtree and its callback calls setAttribute, which fires a mutation
   record even when the value is unchanged — a permanent ~10/sec loop on every
   PDP. Deleting it is a performance fix, not just a cosmetic one.

   Earlier builds of this file carried an ID-escalated CSS override and an
   injected frequency label to fight those two blocks. Both workarounds are gone;
   if either symptom returns, the theme.liquid blocks are still present. */

/**
 * Whether the free-gift card should be shown.
 *
 * To make it expire on its own, replace the body with a date check, e.g.:
 *
 *   const END = Date.parse('2026-08-24T06:59:00Z'); // 23:59 PT Aug 23
 *   return CONFIG.showBenefitCard && Date.now() < END;
 *
 * Note that uses the SHOPPER's clock, not Shopify's, so a device with the wrong
 * date sees the wrong thing. For a hard cutoff, drive it from Liquid instead.
 */
function promoIsLive() {
  return CONFIG.showBenefitCard === true;
}

function el(tag, className, text) {
  const node = document.createElement(tag);
  if (className) node.className = className;
  if (text != null) node.textContent = text;
  return node;
}

/**
 * Reads a money string as a number. Handles thousands separators — the old
 * theme.liquid script used parseFloat("1,299.00"), which silently returns 1.
 * Returns null rather than NaN so callers must handle failure explicitly.
 */
function parseMoney(text) {
  if (!text) return null;
  const cleaned = text.replace(/[^0-9.,]/g, '').replace(/,/g, '');
  const value = Number.parseFloat(cleaned);
  return Number.isFinite(value) ? value : null;
}

/**
 * Derives the pill from the option's own compare-at and discounted price, so
 * changing the selling plan's discount in Loop updates the badge with no code
 * change. Falls back to CONFIG.discountLabel if either price is unreadable.
 */
function computeDiscountLabel(option) {
  const compareAt = parseMoney(
    option.querySelector('.loop-widget-purchase-option-compare-at-price')?.textContent
  );
  const price = parseMoney(
    option.querySelector('.loop-widget-purchase-option-price')?.textContent
  );
  if (compareAt === null || price === null) return CONFIG.discountLabel;
  if (compareAt <= 0 || price >= compareAt) return null; // no discount to show

  const pct = Math.round(((compareAt - price) / compareAt) * 100);
  return pct > 0 ? `-${pct}%` : null;
}

function buildPill(label) {
  return el('span', PILL_CLASS, label);
}

function buildCard() {
  const card = el('div', CARD_CLASS);

  // ---- header: copy line + collapse chevron -------------------------------
  const header = el('div', 'templ-benefits-header');

  const copy = el('div', 'templ-benefits-copy');
  copy.append(
    document.createTextNode(CONFIG.headerPrefix + ' '),
    el('strong', null, CONFIG.headerStrong),
    document.createTextNode(' ' + CONFIG.headerMiddle + ' '),
    el('span', 'templ-benefits-pct', CONFIG.discountLabel),
    document.createTextNode(' ' + CONFIG.headerSuffix)
  );

  const toggle = el('button', 'templ-benefits-toggle');
  toggle.type = 'button';
  toggle.setAttribute('aria-label', 'Toggle subscription gift details');
  toggle.innerHTML =
    '<svg width="13" height="7" viewBox="0 0 13 7" fill="none" aria-hidden="true">' +
    '<path d="M0.5 0.5L6.5 6.5L12.5 0.5" stroke="currentColor" stroke-linecap="round" stroke-linejoin="round"/></svg>';

  header.append(copy, toggle);

  // ---- the single gift row ------------------------------------------------
  const list = el('div', 'templ-benefits-list');

  const item = el('div', 'templ-benefit-item');
  const img = el('div', 'templ-benefit-image');
  img.style.backgroundImage = `url("${CONFIG.gift.image}")`;
  img.setAttribute('role', 'img');
  img.setAttribute('aria-label', CONFIG.gift.title);

  const content = el('div', 'templ-benefit-content');
  const price = el('div', 'templ-benefit-price');
  price.append(
    el('span', 'templ-benefit-was', CONFIG.gift.wasPrice),
    el('span', 'templ-benefit-now', CONFIG.gift.nowPrice)
  );
  content.append(
    el('div', 'templ-benefit-title', CONFIG.gift.title),
    price
  );

  item.append(img, content);
  list.append(item);

  // ---- footer strip -------------------------------------------------------
  const footer = el('div', 'templ-benefits-footer');
  const wrap = el('div', 'templ-bf-wrap');
  CONFIG.footerItems.forEach((textItem, i) => {
    if (i > 0) wrap.append(el('span', 'templ-bf-sep'));
    wrap.append(el('div', 'templ-bf-item', textItem));
  });
  footer.append(wrap);

  card.append(header, list, footer);

  // Collapse behaviour, matching Cadence's chevron.
  const setExpanded = (expanded) => {
    toggle.classList.toggle('expanded', expanded);
    list.classList.toggle('templ-hidden', !expanded);
    footer.classList.toggle('templ-hidden', !expanded);
    toggle.setAttribute('aria-expanded', String(expanded));
  };
  setExpanded(CONFIG.startExpanded);
  header.addEventListener('click', () => setExpanded(!toggle.classList.contains('expanded')));

  return card;
}

/** Idempotent. Adds what should exist, removes what shouldn't. */
function sync() {
  const option = document.querySelector(SUB_OPTION);
  if (!option) return;

  // 1. discount pill, derived from the live prices
  const labelWrap = option.querySelector(LABEL_WRAP);
  if (labelWrap) {
    const label = computeDiscountLabel(option);
    const existingPill = labelWrap.querySelector(`.${PILL_CLASS}`);
    if (!label) {
      existingPill?.remove();
    } else if (!existingPill) {
      labelWrap.appendChild(buildPill(label));
    } else if (existingPill.textContent !== label) {
      // textContent only — never setAttribute here. Writing an attribute from
      // inside a MutationObserver callback fires a record even when the value
      // is unchanged, which is how the old theme.liquid script self-triggered
      // into a permanent ~10/sec loop.
      existingPill.textContent = label;
    }
  }

  // 2. shorten the frequency option text so the row reads "Deliver Every: 30 days"
  //    like Cadence's. Only visible text is rewritten — option values are never
  //    touched, so Loop still posts the correct selling plan.
  const select = option.querySelector('select.loop-widget-sp-selector');
  if (select) {
    for (const opt of select.options) {
      const short = opt.textContent.replace(/^\s*deliver\s+every\s*/i, '').trim();
      if (short && short !== opt.textContent) opt.textContent = short;
    }
  }

  // 3. benefit card — promo-gated
  const mount = option.querySelector(MOUNT);
  if (!mount) return;
  const existing = mount.querySelector(`:scope > .${CARD_CLASS}`);

  if (!promoIsLive()) {
    existing?.remove();
    // Loop's own description lines come back when the card is gone.
    option.classList.remove('templ-has-benefits');
    return;
  }
  option.classList.add('templ-has-benefits');
  if (existing) return; // already correct — observer settles here
  mount.appendChild(buildCard());
}

function runSync() {
  try {
    sync();
  } catch (error) {
    // Never let a presentation bug surface to the shopper.
    console.warn('[templ-widget]', error);
  }
}

/**
 * A single rAF-debounced pass is NOT enough.
 *
 * Verified on GENESIS (3 flavours) Aug 16: changing variant makes Loop tear the
 * widget down and rebuild it over multiple frames. One rAF pass runs while that
 * rebuild is still in flight — it injects into DOM that Loop then replaces, and
 * because the replacement produces no further mutation we care about, nothing
 * ever re-runs. Result: pill gone, card gone, option text reverted to Loop's
 * "Deliver every 30 days". Manual sync() recovered it instantly, which is what
 * identified this as a timing race rather than a dead observer.
 *
 * So each mutation burst schedules an immediate pass plus trailing ones.
 * sync() is idempotent, so extra passes cost nothing and settle silently.
 *
 * The timers are cleared and reset per burst, so this stays bounded — it cannot
 * become the runaway loop the old theme.liquid script fell into. Note also that
 * we deliberately do NOT observe attributes on the widget: sync() adds a class,
 * which would retrigger the observer forever.
 */
const TRAILING_PASSES_MS = [300, 900, 1800, 3000];
let rafQueued = false;
let trailingTimers = [];

function scheduleSync() {
  if (!rafQueued) {
    rafQueued = true;
    requestAnimationFrame(() => {
      rafQueued = false;
      runSync();
    });
  }
  trailingTimers.forEach(clearTimeout);
  trailingTimers = TRAILING_PASSES_MS.map((ms) => setTimeout(runSync, ms));
}

/**
 * Safety net. Trailing timers alone were NOT reliable: switching to a third
 * flavour on GENESIS wiped the widget again, because when the page is busy the
 * rAF pass gets deferred and the trailing timers all fire while Loop's rebuild
 * is still in flight. After that the page goes quiet and nothing re-runs.
 *
 * Rather than guess a longer delay — any fixed number is wrong on a slow enough
 * device — this re-checks on a slow interval forever. sync() early-returns as
 * soon as it sees the pill and card already present, so the steady-state cost
 * is two querySelectors every two seconds.
 *
 * This is emphatically NOT the pattern that broke theme.liquid: that observed
 * {attributes: true} across the whole body AND wrote an attribute from inside
 * its own callback, retriggering itself ~10x/sec. This is a fixed 0.5Hz timer
 * that writes nothing when the DOM is already correct.
 */
const SAFETY_INTERVAL_MS = 2000;
setInterval(runSync, SAFETY_INTERVAL_MS);

new MutationObserver(scheduleSync).observe(document.body, { childList: true, subtree: true });

// Variant swatches and the frequency select both surface as change events.
// Capture phase so we see them even if a handler stops propagation.
document.addEventListener('change', scheduleSync, true);

document.addEventListener('DOMContentLoaded', scheduleSync);
scheduleSync();
